import React from 'react';

import ProcedureRunDesignFlowchartEditor from 'studies/typed/shared/flowcharts/design/ProcedureRunDesignFlowchartEditor';
import {useProcedureRunDesignFlowchartEdgeSpecs} from 'studies/typed/shared/flowcharts/design/useProcedureRunDesignFlowchartEdgeSpecs';
import {ConditionGroup} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import {UseStudyDesignStateResult} from 'studies/typed/shared/procedure-runs/design-state/useStudyDesignState';

import PublishedProcedureVersionUpdateBanner from '../procedure-selection/PublishedProcedureVersionUpdateBanner';
import {FlowchartProcedureRunDesignOptOutBanner} from './FlowchartProcedureRunDesignPreviewBanners';
import ProcedureRunDesignEditorLeftSidepanel from './ProcedureRunDesignEditorLeftSidepanel';
import ProcedureRunDesignEditorRightSidepanel from './ProcedureRunDesignEditorRightSidepanel';
import {DataEntryProps, ProcedureProps, VersionUpdateProps} from './ProcedureRunPlanDesignContentTypes';
import {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';

interface PreviewContext {
  isOptOutBannerDismissed: boolean;
  onDismissOptOutBanner: () => void;
  onOptOut: () => Promise<void>;
}

interface FlowchartProcedureRunPlanDesignContentProps {
  dataEntry: DataEntryProps;
  designState: UseStudyDesignStateResult<ConditionGroup>;
  onRemoveProcedure: () => Promise<void>;
  preview: PreviewContext;
  procedure: ProcedureProps;
  versionUpdate: VersionUpdateProps;
}

export default function FlowchartProcedureRunPlanDesignContent({
  dataEntry,
  designState,
  onRemoveProcedure,
  preview,
  procedure,
  versionUpdate,
}: FlowchartProcedureRunPlanDesignContentProps): JSX.Element {
  const {
    conditions,
    context,
    editState,
    experimentalInputs,
    methodExecutionLimitState,
    selectedMethodId,
    setConditions,
    setExperimentalInputs,
    setSelectedMethodId,
    templateFlowchart,
  } = designState;

  // Pre-promotion there's no per-run condition graph, so planning edges fan the template method
  // graph out across each condition/replicate row to show the execution order in the flowchart.
  const edgeSpecs = useProcedureRunDesignFlowchartEdgeSpecs({
    isFlowchartProcedureRunDesignEnabled: true,
    conditions,
    methods: templateFlowchart.nodes,
    procedureEdges: templateFlowchart.edges,
  });

  return (
    <ProcedureRunDesignFlowchartEditor
      conditions={conditions}
      methods={templateFlowchart.nodes}
      activeMethodId={null} // Active method is not relevant for run plans
      procedureType={procedure.type}
      experimentalInputs={experimentalInputs}
      selectedMethodId={selectedMethodId}
      onChangeSelectedMethod={setSelectedMethodId}
      isInEditState={editState === StudyDesignTabEditState.EDITING}
      isPlan={true}
      edgeSpecs={edgeSpecs}
      allowEditingPersistedConditions={true}
      topBanner={
        !preview.isOptOutBannerDismissed || versionUpdate.hasNewerVersion ? (
          <>
            {!preview.isOptOutBannerDismissed && (
              <FlowchartProcedureRunDesignOptOutBanner
                isPlan={true}
                onDismiss={preview.onDismissOptOutBanner}
                onOptOut={preview.onOptOut}
                procedureType={procedure.type}
              />
            )}
            {versionUpdate.hasNewerVersion && (
              <PublishedProcedureVersionUpdateBanner
                procedureType={procedure.type}
                onOpenUpdateModal={versionUpdate.onOpenUpdateModal}
                hasUnsavedChanges={versionUpdate.hasUnsavedChanges}
              />
            )}
          </>
        ) : null
      }
      leftPanelChildren={
        <ProcedureRunDesignEditorLeftSidepanel
          conditions={conditions}
          setConditions={setConditions}
          experimentalInputs={experimentalInputs}
          setExperimentalInputs={setExperimentalInputs}
          editState={editState}
          procedureType={procedure.type}
          methodExecutionLimitState={methodExecutionLimitState}
          studyType={procedure.studyType}
          dataEntryMode={dataEntry.mode}
          setDataEntryMode={dataEntry.setMode}
          procedureId={procedure.id}
          flowchartVersionId={procedure.flowchartVersionId}
          onRemoveProcedure={onRemoveProcedure}
          hasUpdateAvailable={versionUpdate.hasNewerVersion}
          isProcedureVersionUpdateDisabled={versionUpdate.hasUnsavedChanges}
          onOpenUpdateModal={versionUpdate.onOpenUpdateModal}
          isPlan={true}
        />
      }
      // Render the Design panel contents only once a method is selected: the sidepanel requires a
      // non-null selectedMethodId, and the editor keeps the right panel closed without a selection.
      rightPanelChildren={
        selectedMethodId != null && (
          <ProcedureRunDesignEditorRightSidepanel
            templateFlowchart={templateFlowchart}
            selectedMethodId={selectedMethodId}
            procedureType={procedure.type}
            context={context}
            isProcedureRunPlan={true}
          />
        )
      }
    />
  );
}
